import React, { useState } from 'react';
import { Wifi, WifiOff, AlertTriangle, X } from 'lucide-react';

const EspConnection = ({
  isConnected,
  isConnecting,
  error,
  deviceName,
  isBluetoothAvailable,
  isSecureContext,
  connect,
  disconnect
}) => {
  const [showErrorHelp, setShowErrorHelp] = useState(false);

  return (
    <div className="w-full flex items-center justify-between bg-slate-800/60 backdrop-blur-md border border-slate-700/50 rounded-xl px-4 py-2 mb-2 shadow-sm transition-all hover:bg-slate-800/80">
      {/* Izquierda: Estado/Botón */}
      <div className="flex items-center gap-3 min-w-0">
        {!isBluetoothAvailable ? (
           <div className="flex items-center gap-2">
             <button 
               onClick={() => setShowErrorHelp(!showErrorHelp)}
               className="flex items-center gap-1.5 text-red-400 bg-red-500/10 border border-red-500/20 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-red-500/20 transition-all"
             >
               <AlertTriangle size={14} />
               <span>Bluetooth no disponible</span>
             </button>
           </div>
        ) : (
          <button
            onClick={isConnected ? disconnect : connect}
            disabled={isConnecting}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 border min-w-0 max-w-[180px] ${
              isConnected
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20'
                : 'bg-blue-600 text-white border-transparent hover:bg-blue-500 shadow-sm hover:shadow hover:scale-[1.02]'
            } disabled:opacity-50 disabled:hover:scale-100`}
          >
            {isConnecting ? (
              <span className="animate-spin block shrink-0">⚙️</span>
            ) : isConnected ? (
              <Wifi size={16} className="shrink-0" />
            ) : (
              <WifiOff size={16} className="shrink-0" />
            )}
            <span className="hidden sm:block truncate">
               {isConnecting ? 'Conectando...' : isConnected ? (deviceName || 'Conectado') : 'Conectar Máquina'}
            </span>
          </button>
        )}
        
        {/* Error mensaje pequeño al lado del botón */}
        {error && !isConnected && (
            <span className="text-red-400 text-xs flex items-center gap-1 animate-fadeIn bg-red-950/30 px-2 py-1 rounded border border-red-900/30" title={error}>
              <AlertTriangle size={10} /> {error}
            </span>
        )}
      </div>

      {/* Derecha: indicador de estado */}
      {isConnected && (
        <div className="hidden md:flex items-center gap-2 px-2 py-1 bg-slate-700/30 rounded text-xs text-slate-400 border border-slate-700/50 shrink-0">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_5px_rgba(16,185,129,0.5)]"></div>
          <span>En línea</span>
        </div>
      )}

      {/* Modal de Ayuda Bluetooth superpuesto */}
      {showErrorHelp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={() => setShowErrorHelp(false)}>
          <div className="bg-slate-800 border border-slate-700 p-6 rounded-2xl max-w-md w-full shadow-2xl relative scale-100 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
             <button onClick={() => setShowErrorHelp(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"><X size={20}/></button>
             
             <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <div className="bg-red-500/20 p-2 rounded-lg text-red-400"><AlertTriangle size={24}/></div>
                Problemas de Conexión
             </h3>
             
             {!isSecureContext ? (
               <div className="space-y-4 text-slate-300 text-sm">
                 <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-700/50">
                    <p className="font-semibold text-red-300 mb-1">Conexión no segura detectada</p>
                    <p className="text-xs text-slate-400">Chrome bloquea Bluetooth en sitios HTTP que no son localhost.</p>
                 </div>
                 
                 <div className="space-y-2">
                    <p>Para solucionar esto:</p>
                    <ol className="list-decimal pl-5 space-y-2 text-xs text-slate-400">
                    <li>Copia y abre esta dirección en una nueva pestaña: <br/><code className="bg-black/40 px-2 py-1 rounded block mt-1 select-all text-blue-300 border border-slate-700">chrome://flags/#unsafely-treat-insecure-origin-as-secure</code></li>
                    <li>Cambia la opción a <strong className="text-white">Enabled</strong></li>
                    <li>Agrega la dirección IP de esta página a la lista blanca que aparece.</li>
                    <li>Reinicia el navegador con el botón "Relaunch".</li>
                    </ol>
                 </div>
               </div>
             ) : (
               <div className="space-y-4 text-slate-300 text-sm">
                 <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-700/50">
                    <p className="font-semibold text-red-300 mb-1">Navegador no compatible</p>
                    <p className="text-xs text-slate-400">Tu navegador actual no soporta la API Web Bluetooth.</p>
                 </div>

                 <ul className="space-y-3">
                   <li className="flex gap-3 items-start">
                     <span className="bg-slate-700 text-white text-xs px-2 py-0.5 rounded mt-0.5">iOS</span>
                     <span className="text-xs text-slate-400">Chrome y Safari en iPhone/iPad <strong>NO</strong> funcionan. Descarga la app gratuita <strong>"Bluefy"</strong> en la App Store.</span>
                   </li>
                   <li className="flex gap-3 items-start">
                     <span className="bg-slate-700 text-white text-xs px-2 py-0.5 rounded mt-0.5">Android</span>
                     <span className="text-xs text-slate-400">Asegúrate de tener Android 6.0+ y Chrome actualizado.</span>
                   </li>
                 </ul>
               </div>
             )}
             
             <div className="mt-6 flex justify-end">
                <button onClick={() => setShowErrorHelp(false)} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors">
                    Entendido
                </button>
             </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default EspConnection;
